<template>
  <div class="h-full flex flex-col rounded-2xl overflow-hidden bg-white dark:bg-dark-800 border border-green-200 dark:border-green-800 shadow-sm">
    <!-- Header -->
    <div class="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 dark:from-green-500/20 dark:to-emerald-500/20 border-b border-green-200/50 dark:border-green-800/30">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white shadow-lg">
            <i class="fas fa-money-bill-wave"></i>
          </div>
          <div>
            <h3 class="text-sm font-bold text-gray-900 dark:text-white">
              Payments
            </h3>
            <p class="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
              <span>{{ paymentDetails?.length || 0 }} transactions</span>
              <span
                v-if="pendingApprovals > 0"
                class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold"
              >
                <i class="fas fa-clock mr-1"></i>{{ pendingApprovals }}
              </span>
            </p>
          </div>
        </div>
        <div class="text-right">
          <p class="text-xs text-gray-600 dark:text-gray-400 mb-0.5">Total Received</p>
          <p class="text-lg font-black bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-400 dark:to-emerald-400 bg-clip-text text-transparent">
            {{ formatCurrency(paymentDetails?.reduce((sum, p) => sum + Number(p.amount), 0) || 0) }}
          </p>
        </div>
      </div>

      <!-- Breakdown Stats -->
      <div v-if="canViewBreakdown" class="grid grid-cols-3 gap-2">
        <div class="bg-white dark:bg-dark-700 p-2 rounded-lg text-center border border-green-200 dark:border-green-800">
          <p class="text-[10px] text-gray-600 dark:text-gray-400 mb-1">Cash</p>
          <p class="text-xs font-bold text-green-600 dark:text-green-400">
            {{ formatCurrency(paymentDetails?.reduce((sum, p) => 
              p.payment_method === 'cash' && p.payment_type !== 'Collection' ? sum + Number(p.amount) : sum, 0) || 0) }}
          </p>
        </div>
        <div class="bg-white dark:bg-dark-700 p-2 rounded-lg text-center border border-blue-200 dark:border-blue-800">
          <p class="text-[10px] text-gray-600 dark:text-gray-400 mb-1">Online</p>
          <p class="text-xs font-bold text-blue-600 dark:text-blue-400">
            {{ formatCurrency(paymentDetails?.reduce((sum, p) => 
              (p.payment_method === 'bank_transfer' || p.payment_method === 'credit' || p.payment_method === 'check') && p.payment_type !== 'Collection' ? sum + Number(p.amount) : sum, 0) || 0) }}
          </p>
        </div>
        <div class="bg-white dark:bg-dark-700 p-2 rounded-lg text-center border border-indigo-200 dark:border-indigo-800">
          <p class="text-[10px] text-gray-600 dark:text-gray-400 mb-1">Collection</p>
          <p class="text-xs font-bold text-indigo-600 dark:text-indigo-400">
            {{ formatCurrency(paymentDetails?.reduce((sum, p) => {
              const isCollection = p.payment_type === 'Collection';
              return isCollection ? sum + Number(p.amount) : sum;
            }, 0) || 0) }}
          </p>
        </div>
      </div>
    </div>
    <!-- Content -->
    <div class="flex-1 overflow-hidden p-3">
      <!-- Loading State -->
      <div v-if="loading" class="flex flex-col items-center justify-center h-full">
        <div class="relative">
          <div class="w-10 h-10 rounded-full border-4 border-green-200 dark:border-green-800 border-t-green-600 dark:border-t-green-400 animate-spin"></div>
          <i class="fas fa-money-bill-wave absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-green-600 dark:text-green-400 text-sm"></i>
        </div>
        <p class="mt-2 text-xs font-medium text-gray-600 dark:text-gray-400">Loading payments...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="!paymentDetails || paymentDetails.length === 0" class="flex flex-col items-center justify-center h-full text-center">
        <div class="w-16 h-16 rounded-2xl bg-green-100 dark:bg-green-900/20 flex items-center justify-center mb-3">
          <i class="fas fa-inbox text-2xl text-green-600 dark:text-green-400"></i>
        </div>
        <p class="text-xs font-semibold text-gray-900 dark:text-white">No payments found</p>
        <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">No payments for this date</p>
      </div>

      <!-- Payments List -->
      <div v-else class="h-full overflow-y-auto custom-scrollbar space-y-1.5">
        <div v-for="payment in paymentDetails" :key="payment.payment_id" 
             class="group p-2.5 rounded-lg bg-white/50 dark:bg-dark-700/50 border border-green-200/30 dark:border-green-800/20 hover:border-green-300 dark:hover:border-green-700 hover:shadow-md transition-all duration-200 cursor-pointer"
             @click="openPayment(payment.payment_id)">
          <!-- Line 1: Order number + Amount -->
          <div class="flex items-center justify-between gap-2 mb-0.5">
            <p class="text-xs font-semibold text-blue-600 dark:text-blue-400 group-hover:underline truncate">
              {{ payment.order_number }}
            </p>
            <div class="text-xs font-bold text-gray-900 dark:text-white whitespace-nowrap">
              {{ formatCurrency(payment.amount) }}
            </div>
          </div>
          <!-- Line 2: Client name + compact method indicators -->
          <div class="flex items-center justify-between gap-2">
            <p class="text-[10px] text-gray-600 dark:text-gray-400 truncate">
              {{ payment.client_name }}
            </p>
            <div class="flex items-center gap-1.5">
              <!-- Compact payment method icon (no text) -->
              <span
                :class="getPaymentMethodClass(payment.payment_method)"
                class="inline-flex items-center justify-center w-5 h-5 rounded-full text-[8px]">
                <i :class="getPaymentMethodIcon(payment.payment_method)"></i>
              </span>
              <!-- Optional Collection indicator as icon only -->
              <span
                v-if="payment.payment_type === 'Collection'"
                class="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                <i class="fas fa-hand-holding-usd text-[8px]"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { formatCurrency } from '@/utils/formatters';
import { useAuth } from '@/composables/useAuth';

const { hasRole } = useAuth();

// Check if user can view breakdown (super_admin, admin, or manager)
const canViewBreakdown = computed(() => hasRole(['super_admin', 'admin', 'manager']));

const props = defineProps({
  paymentDetails: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  pendingApprovals: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['open-payment']);

const openPayment = (paymentId) => {
  emit('open-payment', paymentId);
};

const truncateText = (text, length = 15) => {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
};

const getPaymentMethodClass = (method) => {
  switch (method?.toLowerCase()) {
    case 'cash':
      return 'bg-green-100 text-green-800';
    case 'bank_transfer':
      return 'bg-blue-100 text-blue-800';
    case 'check':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getPaymentMethodIcon = (method) => {
  switch (method?.toLowerCase()) {
    case 'cash':
      return 'fas fa-money-bill-wave';
    case 'bank_transfer':
      return 'fas fa-university';
    case 'check':
      return 'fas fa-money-check-alt';
    default:
      return 'fas fa-wallet';
  }
};

const formatPaymentMethod = (method) => {
  if (!method) return 'N/A';
  
  // Special case for bank_transfer to display as Online
  if (method.toLowerCase() === 'bank_transfer') {
    return 'Online';
  }
  
  return method.split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
</script> 